import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { AdminService } from 'app/modules/admin/services/admin.service';
import { AuthService } from 'app/modules/auth/services/auth.service';
import { User } from 'app/modules/domain/models/user';
import { UserRole } from 'app/modules/domain/enums/userRole';
import { RoleGuardService } from 'app/modules/auth/services/role.guard';
import { Router } from '@angular/router';
import { MdDialog } from '@angular/material';
import { DeleteUserComponent } from '../delete-user/delete-user.component';
import { AddUserComponent } from '../add-user/add-user.component';
import { Subject } from 'rxjs';

interface ExtendUser {
  id: string;
  name: string;
  email: string;
  companyId: string;
  roles: UserRole[];
  limit?: number;
}
@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss']
})
export class UserListComponent implements OnInit {
  @Input() public companyId: string;

  private _users;

  get users(): any {
    return this._users;
  }

  @Input()
  set users(val: any) {
    this._users = val;
    this.filteredUsers = val;
  }

  @Input() userLimit: number;

  @Output() onUpdateUsers: EventEmitter<void> = new EventEmitter<void>();

  public isSuperAdmin = false;
  public userId: string;
  public errorString: string;
  public filteredUsers: ExtendUser[] = [];

  quotaChanged: Subject<any> = new Subject<any>();


  constructor(private adminService: AdminService,
    private authService: AuthService,
    private roleGuardService: RoleGuardService,
    private router: Router,
    public dialog: MdDialog) { }

  ngOnInit() {
    this.isSuperAdmin = this.roleGuardService.isAuthorized(['SuperAdmin']);

    const token = this.authService.getDecodedJwtToken();
    this.userId = token.sub;

    this.filteredUsers = this.users;

    this.quotaChanged
      .debounceTime(1000) // wait 1 sec after the last event before emitting last event
      .distinctUntilChanged() // only emit if value is different from previous value
      .subscribe(model => {
        this.doQuotaChange(model);
      });
  }

  public onSearch(event): void {
    const searchString = event.target.value.toLocaleLowerCase();
    if (!searchString || searchString === '') {
      this.filteredUsers = this.users;
    } else {
      this.filteredUsers = this.users.filter(u => {
        return u.name.toLocaleLowerCase().includes(searchString) || u.email.toLocaleLowerCase().includes(searchString);
      });
    }
  }

  public onRoleChange(user: User, role: string): void {
    user.roles = this.getRolesArray(role);

    this.adminService.updateUser(user).subscribe((success) => {
      const usr = this.filteredUsers.find(u => u.id === user.id);

      usr.roles = user.roles;
    });
  }

  public onQuotaChange(userId, event) {
    this.quotaChanged.next({ userId, event });
  }

  public doQuotaChange({ userId, event }): void {
    const userObject = this.users.find(u => u.id === userId);
    const quota = event.target.value;

    if (quota !== '') {
      this.adminService.updateUserQuota(this.companyId, userId, quota).subscribe(() => {
        userObject.limit = quota; // If successfull save new limit to userObject
        this.onUpdateUsers.emit();
      }, (err) => {
        const errorKey = Object.keys(err.error)[0];
        this.setError(errorKey ? `UPDATE_USER.${errorKey}` : 'UNKNOWN_ERROR');
        const userObj = this.users.find(u => u.id === userId);
        event.target.value = userObj.limit;  // If not successfull return to previous value
      });
    }
  }

  public impersonate(userId: string): void {
    this.adminService.getImpersonationToken(userId, this.companyId).subscribe((token) => {
      this.authService.setUserToken(token);
      this.router.navigate(['auth']);
    });
  }

  public getMaxUsers(): number | string {
    return this.userLimit !== undefined ? this.userLimit : '&infin;';
  }

  public addUser(): void {
    const dialogRef = this.dialog.open(AddUserComponent, {
      width: '450px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.doAddUser(result);
      }
    });
  }

  private doAddUser(userInput): void {
    this.adminService.createUser({
      companyId: this.companyId,
      email: userInput.email,
      name: userInput.name,
      roles: this.getRolesArray(userInput.role)
    }).subscribe(() => {
      this.onUpdateUsers.emit();
    }, (err) => {
      const errorKey = Object.keys(err.error)[0];
      this.setError(`CREATE_USER.${errorKey}`);
    });
  }

  public deleteUser(user: User): void {
    const dialogRef = this.dialog.open(DeleteUserComponent, {
      width: '450px',
      data: { user }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.doDeleteUser(user.id);
      }
    });
  }

  private doDeleteUser(userId: string): void {
    this.adminService.deleteUser(this.companyId, userId).subscribe(result => {
      this.onUpdateUsers.emit();
    });
  }

  public setError(errorString: string): void {
    this.errorString = errorString;
    setTimeout(function () { this.dismissError(); }.bind(this), 7000);
  }

  public dismissError(): void {
    this.errorString = undefined;
  }

  public userIsSuperAdmin(roles: number[]): boolean {
    return roles.includes(UserRole.SuperAdmin);
  }

  public displayRoles(roles: number[]): string {
    const highestRole = Math.max(...roles);

    return UserRole[highestRole];
  }

  private getRolesArray(role: string): UserRole[] {
    const newRole = UserRole[role];
    if (newRole === UserRole.ReadOnly) {
      return [UserRole.ReadOnly];
    } else if (newRole === UserRole.Basic) {
      return [UserRole.Basic];
    } else if (newRole === UserRole.Admin) {
      return [UserRole.Basic, UserRole.Admin];
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/admin/shared/user-list/user-list.component.ts